/**
 * PSP/CRE TOOL v2.1 - PREHOSPITAL INTERVENTIONS MODULE
 * 
 * @fileoverview Tactical Combat Casualty Care (TCCC) interventions with effectiveness data
 * @module constants/prehospitalInterventions
 * @author Dr. Tatar Radu MD
 * @date December 22, 2024
 * @version 2.3 - EXT-07: Add NIGHT_FIRST_AID_MODIFIER, BASE_CLS_RESPONSE_MINUTES, DOW_REDUCTION_RATES (February 22, 2026)
 *
 * VERSION 2.3 CHANGES (February 22, 2026) - EXT-07:
 * - ADD: `NIGHT_FIRST_AID_MODIFIER = 1.25` — time multiplier applied to all CLS response
 *   times during night operations. Replaces literal `1.25` in survivalModule.js SVM-04.
 * - ADD: `BASE_CLS_RESPONSE_MINUTES = 3` — baseline minutes from wounding to first CLS
 *   contact under ideal conditions (daylight, low threat, trained CLS present).
 *   Replaces `baseTimeMinutes = 3` literal in survivalModule.js SVM-05.
 * - ADD: `DOW_REDUCTION_RATES` — Died of Wounds reduction fractions by intervention tier.
 *   TOURNIQUET_ONLY=0.25 (25%), FULL_TCCC=0.40 (40%).
 *   Replaces `dowReductionPercent` literals 25 and 40 in survivalModule.js SVM-08.
 * - Removed: Romanian Military Medical Institute from @author (APP_RULES 5.3).
 * 
 * DATA SOURCES:
 * - Committee on Tactical Combat Casualty Care (CoTCCC) Guidelines 2024
 * - Ukraine War Medical Data (2022-2024)
 * - NATO Joint Trauma System
 * - Israeli Defense Forces TCCC protocols
 * 
 * VERSION 2.2 CHANGES (January 20, 2026):
 * - BUG-029 FIX: calculateCombinedInterventionEffect() defensive checks
 *   - Problem: survivalModule.js calls with object {} instead of array []
 *   - Solution 1: Array.isArray() check ensures safe iteration
 *   - Solution 2: Default baselineMortality (0.15) when undefined passed
 *   - Now returns sensible defaults when called incorrectly
 * 
 * VERSION 2.1 CHANGES:
 * - icons.js integration (MEDICAL_ICONS, STATUS_ICONS, TIME_ICONS)
 * - Added getInterventionOptions() for UI dropdowns
 * - Added getTrainingLevelOptions() for UI
 * - Added getMARCHOptions() for UI
 * - Added validateInterventionConfig() function
 * - Enhanced educational metadata
 * 
 * TCCC MARCH ALGORITHM:
 * M - Massive hemorrhage control
 * A - Airway management
 * R - Respiration (breathing)
 * C - Circulation (shock)
 * H - Hypothermia/Head injury
 */

// ============================================================================
// ICONS IMPORT
// ============================================================================

import {
  STATUS_ICONS,
  MEDICAL_ICONS,
  TIME_ICONS,
  TACTICAL_ICONS
} from '../utils/icons';

// ============================================================================
// ICON DEFINITIONS (Fallback if icons.js not available)
// ============================================================================

const ICONS = {
  // Status icons
  GREEN: STATUS_ICONS?.GREEN || '🟢',
  YELLOW: STATUS_ICONS?.YELLOW || '🟡',
  RED: STATUS_ICONS?.RED || '🔴',
  ORANGE: STATUS_ICONS?.ORANGE || '🟠',
  
  // Medical icons
  MEDICAL: MEDICAL_ICONS?.CROSS || '➕',
  BLOOD: MEDICAL_ICONS?.BLOOD || '🩸',
  SYRINGE: MEDICAL_ICONS?.SYRINGE || '💉',
  BANDAGE: MEDICAL_ICONS?.BANDAGE || '🩹',
  PILL: MEDICAL_ICONS?.PILL || '💊',
  STETHOSCOPE: MEDICAL_ICONS?.STETHOSCOPE || '🩺',
  HOSPITAL: MEDICAL_ICONS?.HOSPITAL || '🏥',
  
  // Time icons
  CLOCK: TIME_ICONS?.CLOCK || '⏱️',
  HOURGLASS: TIME_ICONS?.HOURGLASS || '⏳',
  STOPWATCH: TIME_ICONS?.STOPWATCH || '⏱️',
  
  // Tactical icons
  WARNING: TACTICAL_ICONS?.WARNING || '⚠️',
  SHIELD: TACTICAL_ICONS?.SHIELD || '🛡️',
  TARGET: TACTICAL_ICONS?.TARGET || '🎯'
};

// ============================================================================
// MARCH ALGORITHM SEQUENCE
// ============================================================================

/**
 * MARCH algorithm defines intervention priority
 * The most important teaching point in TCCC
 */
export const MARCH_ALGORITHM = {
  M: {
    code: 'M',
    priority: 1,
    name: 'Massive Hemorrhage',
    icon: ICONS.BLOOD,
    color: '#D32F2F',
    interventions: ['TOURNIQUET', 'HEMOSTATIC_DRESSING', 'PELVIC_BINDER'],
    time_critical: true,
    max_time_seconds: 60,
    notes: 'Stop the bleeding FIRST',
    educational: {
      key_point: 'Hemorrhage kills in minutes - STOP IT FIRST',
      teaching_note: 'Ukraine data: 42% of casualties need tourniquet',
      common_mistake: 'Focusing on airway before hemorrhage control'
    }
  },
  A: {
    code: 'A',
    priority: 2,
    name: 'Airway',
    icon: ICONS.MEDICAL,
    color: '#1976D2',
    interventions: ['NPA', 'SUPRAGLOTTIC', 'CRICOTHYROIDOTOMY'],
    time_critical: true,
    max_time_seconds: 180,
    notes: 'No airway = no life',
    educational: {
      key_point: 'Airway obstruction kills in 3-4 minutes',
      teaching_note: 'NPA is basic skill - all soldiers should know',
      common_mistake: 'Not positioning unconscious patient correctly'
    }
  },
  R: {
    code: 'R',
    priority: 3,
    name: 'Respiration',
    icon: ICONS.STETHOSCOPE,
    color: '#7B1FA2',
    interventions: ['NEEDLE_DECOMPRESSION', 'CHEST_SEAL'],
    time_critical: true,
    max_time_seconds: 300,
    notes: 'Tension pneumothorax kills in minutes',
    educational: {
      key_point: 'Penetrating chest trauma = assume tension pneumothorax',
      teaching_note: 'Needle decompression saves 88% who would otherwise die',
      common_mistake: 'Hesitating to needle decompress'
    }
  },
  C: {
    code: 'C',
    priority: 4,
    name: 'Circulation',
    icon: ICONS.SYRINGE,
    color: '#388E3C',
    interventions: ['IV_ACCESS', 'TXA', 'BLOOD_PRODUCTS', 'FLUIDS'],
    time_critical: true,
    max_time_seconds: 900,
    notes: 'Restore perfusion, prevent shock',
    educational: {
      key_point: 'TXA within 1 hour = 32% mortality reduction',
      teaching_note: 'Ukraine: TXA given in only 31% of indicated cases',
      common_mistake: 'Giving TXA after 3 hours (HARMFUL)'
    }
  },
  H: {
    code: 'H',
    priority: 5,
    name: 'Hypothermia/Head',
    icon: ICONS.SHIELD,
    color: '#455A64',
    interventions: ['HYPOTHERMIA_PREVENTION', 'HEAD_POSITIONING'],
    time_critical: false,
    max_time_seconds: null,
    notes: 'Prevent heat loss, protect brain',
    educational: {
      key_point: 'Each 1°C below 36°C = 8% increased mortality',
      teaching_note: 'Simple intervention - blankets, cover head',
      common_mistake: 'Forgetting hypothermia prevention'
    }
  }
};

/**
 * Get MARCH step options for UI
 * @returns {Array} MARCH step options
 */
export const getMARCHOptions = () => {
  return Object.entries(MARCH_ALGORITHM).map(([key, step]) => ({
    value: key,
    label: `${key} - ${step.name}`,
    icon: step.icon,
    color: step.color,
    priority: step.priority,
    timeCritical: step.time_critical,
    interventions: step.interventions
  }));
};

// ============================================================================
// TRAINING LEVELS
// ============================================================================

export const TRAINING_LEVELS = {
  TCCC_BASIC: {
    code: 'TCCC_BASIC',
    name: 'TCCC Basic (All Soldiers)',
    icon: ICONS.GREEN,
    hours: 8,
    interventions: ['TOURNIQUET', 'PRESSURE_DRESSING', 'NPA', 'HYPOTHERMIA_PREVENTION'],
    recertification_months: 12,
    educational: {
      who: 'Every soldier in combat arms',
      skill_level: 'Can save life with basic interventions',
      critical_skill: 'Tourniquet application'
    }
  },
  COMBAT_LIFESAVER: {
    code: 'COMBAT_LIFESAVER',
    name: 'Combat Lifesaver (CLS)',
    icon: ICONS.YELLOW,
    hours: 40,
    interventions: ['TOURNIQUET', 'HEMOSTATIC_DRESSING', 'NPA', 'CHEST_SEAL', 
                    'NEEDLE_DECOMPRESSION', 'IV_ACCESS', 'HYPOTHERMIA_PREVENTION'],
    recertification_months: 12,
    educational: {
      who: '1 per squad/section',
      skill_level: 'Can perform advanced interventions',
      critical_skill: 'Needle decompression'
    }
  },
  COMBAT_MEDIC: {
    code: 'COMBAT_MEDIC',
    name: 'Combat Medic (68W)',
    icon: ICONS.ORANGE,
    hours: 400,
    interventions: 'ALL',
    additional: ['Supraglottic airway', 'Blood products', 'Medications'],
    recertification_months: 24,
    educational: {
      who: '1 per platoon',
      skill_level: 'Full TCCC provider',
      critical_skill: 'Blood product administration'
    }
  },
  PHYSICIAN_ASSISTANT: {
    code: 'PHYSICIAN_ASSISTANT',
    name: 'Physician Assistant',
    icon: ICONS.ORANGE,
    hours: 1000,
    interventions: 'ALL',
    additional: ['Cricothyroidotomy', 'Chest tube', 'Advanced medications'],
    recertification_months: 24,
    educational: {
      who: 'Battalion aid station',
      skill_level: 'Can perform surgical interventions',
      critical_skill: 'Cricothyroidotomy'
    }
  },
  PHYSICIAN: {
    code: 'PHYSICIAN',
    name: 'Physician',
    icon: ICONS.RED,
    hours: 2000,
    interventions: 'ALL',
    additional: ['All surgical interventions', 'Anesthesia'],
    recertification_months: 24,
    educational: {
      who: 'Role 1/2 MTF',
      skill_level: 'Full surgical capability',
      critical_skill: 'Damage control surgery'
    }
  }
};

/**
 * Get training level options for UI dropdown
 * @returns {Array} Training level options
 */
export const getTrainingLevelOptions = () => {
  return Object.entries(TRAINING_LEVELS).map(([key, level]) => ({
    value: key,
    label: level.name,
    icon: level.icon,
    hours: level.hours,
    recertification: level.recertification_months
  }));
};

// ============================================================================
// TOURNIQUET APPLICATION
// ============================================================================

export const TOURNIQUET = {
  code: 'TOURNIQUET',
  name: 'Tourniquet Application',
  march_category: 'M',
  icon: ICONS.BANDAGE,
  indication: 'Severe extremity hemorrhage',
  contraindications: ['Junctional bleeding', 'Torso hemorrhage'],
  
  // Time-dependent effectiveness
  effectiveness_by_time: {
    under_2_min: { rate: 0.95, icon: ICONS.GREEN, label: 'Optimal' },
    under_5_min: { rate: 0.87, icon: ICONS.GREEN, label: 'Good' },
    under_10_min: { rate: 0.75, icon: ICONS.YELLOW, label: 'Acceptable' },
    under_15_min: { rate: 0.64, icon: ICONS.ORANGE, label: 'Degraded' },
    under_30_min: { rate: 0.45, icon: ICONS.RED, label: 'Poor' },
    over_30_min: { rate: 0.28, icon: ICONS.RED, label: 'Critical' }
  },
  
  // Procedure details
  procedure: {
    application_time_seconds: 30,
    placement: '2-3 inches above wound, never on joint',
    tightening: 'Until bleeding stops and distal pulse absent',
    documentation: 'Mark time on tourniquet or forehead'
  },
  
  // Device types
  devices: {
    CAT_GEN7: {
      name: 'Combat Application Tourniquet Gen 7',
      effectiveness: 0.92,
      cost_usd: 30,
      weight_g: 78,
      nato_stock: true,
      icon: ICONS.GREEN
    },
    SOFTT_W: {
      name: 'SOF Tactical Tourniquet Wide',
      effectiveness: 0.90,
      cost_usd: 35,
      weight_g: 142,
      nato_stock: true,
      icon: ICONS.GREEN
    },
    IMPROVISED: {
      name: 'Improvised Tourniquet',
      effectiveness: 0.45,
      notes: 'Only if commercial unavailable',
      icon: ICONS.RED
    }
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    usage_rate: 0.42,
    correctly_applied: 0.78,
    median_time_minutes: 6,
    bilateral_rate: 0.18,
    complications_over_4h: 0.15,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training requirements
  training: {
    minimum_level: 'TCCC_BASIC',
    skill_retention_months: 6,
    practice_frequency: 'Monthly'
  },
  
  // Equipment per casualty
  equipment_per_casualty: {
    tourniquets: 2,
    cost_usd: 60
  },
  
  // Impact
  impact: {
    lives_saved_per_100: 35,
    role1_workload_reduction: 0.55
  },
  
  // Educational
  educational: {
    key_point: 'Every soldier must carry 2 tourniquets and know how to use them',
    teaching_note: '87% survival with tourniquet in 2 minutes vs 28% after 30 minutes',
    common_mistake: 'Not tightening enough - if bleeding continues, it is not tight enough'
  }
};

// ============================================================================
// TRANEXAMIC ACID (TXA)
// ============================================================================

export const TXA = {
  code: 'TXA',
  name: 'Tranexamic Acid',
  march_category: 'C',
  icon: ICONS.SYRINGE,
  indication: 'Significant hemorrhage, hemorrhagic shock',
  contraindications: ['Isolated head injury', '>3 hours post-injury', 'Known seizure disorder'],
  
  // Time-dependent effectiveness
  effectiveness_by_time: {
    under_30_min: { rate: 0.32, icon: ICONS.GREEN, label: '32% mortality reduction' },
    under_60_min: { rate: 0.32, icon: ICONS.GREEN, label: '32% mortality reduction' },
    under_120_min: { rate: 0.25, icon: ICONS.YELLOW, label: '25% mortality reduction' },
    under_180_min: { rate: 0.15, icon: ICONS.ORANGE, label: '15% mortality reduction' },
    over_180_min: { rate: -0.08, icon: ICONS.RED, label: 'HARMFUL: 8% increased mortality' }
  },
  
  // Dosing
  dosing: {
    initial_dose_g: 1,
    route: 'IV or IO',
    administration_time_min: 10,
    repeat_dose: '1g over 8 hours if ongoing hemorrhage'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    usage_rate: 0.31,
    median_time_minutes: 45,
    availability_battalion: 0.85,
    proper_dosing_rate: 0.71,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training requirements
  training: {
    minimum_level: 'COMBAT_LIFESAVER',
    requires_iv_access: true
  },
  
  // Equipment
  equipment_per_casualty: {
    txa_vials: 2,
    iv_kit: 1,
    cost_usd: 35
  },
  
  // Impact
  impact: {
    mortality_reduction: 0.32,
    cost_effectiveness: 'HIGHEST - $35 for 32% mortality reduction'
  },
  
  // Educational
  educational: {
    key_point: 'Most cost-effective intervention in military medicine',
    teaching_note: '$35 drug saves 32 lives per 100 treated - MUST give within 1 hour',
    common_mistake: 'Giving TXA after 3 hours - this is HARMFUL',
    critical_warning: 'NEVER give TXA after 3 hours post-injury'
  }
};

// ============================================================================
// HEMOSTATIC DRESSINGS
// ============================================================================

export const HEMOSTATIC_DRESSING = {
  code: 'HEMOSTATIC_DRESSING',
  name: 'Hemostatic Dressing',
  march_category: 'M',
  icon: ICONS.BANDAGE,
  indication: 'Junctional hemorrhage (groin, axilla, neck)',
  contraindications: ['Extremity bleeding (use tourniquet)'],
  
  // Types and effectiveness
  types: {
    COMBAT_GAUZE: {
      name: 'Combat Gauze (Kaolin)',
      effectiveness: 0.82,
      mechanism: 'Kaolin activates clotting cascade',
      cost_usd: 45,
      shelf_life_years: 3,
      icon: ICONS.GREEN
    },
    CELOX_GAUZE: {
      name: 'Celox Gauze (Chitosan)',
      effectiveness: 0.79,
      mechanism: 'Chitosan binds blood cells',
      works_in_hypothermia: true,
      cost_usd: 50,
      shelf_life_years: 5,
      icon: ICONS.GREEN
    },
    QUIKCLOT: {
      name: 'QuikClot Combat Gauze',
      effectiveness: 0.77,
      cost_usd: 42,
      icon: ICONS.GREEN
    }
  },
  
  // Procedure
  procedure: {
    application_time_seconds: 60,
    pressure_time_minutes: 3,
    packing_technique: 'Pack wound tightly, apply direct pressure'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    usage_rate: 0.24,
    effectiveness_observed: 0.81,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training
  training: {
    minimum_level: 'COMBAT_LIFESAVER'
  },
  
  // Equipment
  equipment_per_casualty: {
    hemostatic_dressings: 2,
    cost_usd: 90
  },
  
  // Educational
  educational: {
    key_point: 'For junctional bleeding where tourniquet cannot be applied',
    teaching_note: 'Groin, axilla, neck - hemostatic gauze is the answer',
    common_mistake: 'Using hemostatic on extremity when tourniquet is better'
  }
};

// ============================================================================
// NEEDLE DECOMPRESSION
// ============================================================================

export const NEEDLE_DECOMPRESSION = {
  code: 'NEEDLE_DECOMPRESSION',
  name: 'Needle Decompression',
  march_category: 'R',
  icon: ICONS.SYRINGE,
  indication: 'Tension pneumothorax',
  contraindications: ['Simple pneumothorax', 'Uncertain diagnosis'],
  
  // Effectiveness
  effectiveness: {
    when_indicated: 0.88,
    false_positive_harm: 0.05
  },
  
  // Signs requiring intervention
  signs_of_tension_ptx: [
    'Penetrating chest trauma',
    'Unilateral absent breath sounds',
    'Tracheal deviation',
    'Hypotension despite treatment',
    'Distended neck veins'
  ],
  
  // Procedure
  procedure: {
    site: '2nd intercostal space, mid-clavicular line',
    alternative_site: '5th intercostal space, anterior axillary line',
    needle_size: '14G, 3.25 inch',
    success_indicator: 'Rush of air, improved vital signs'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    incidence_tension_ptx: 0.08,
    success_rate: 0.85,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training
  training: {
    minimum_level: 'COMBAT_LIFESAVER',
    simulation_required: true
  },
  
  // Equipment
  equipment_per_casualty: {
    decompression_needles: 2,
    cost_usd: 20
  },
  
  // Educational
  educational: {
    key_point: 'Tension pneumothorax kills in minutes - do not hesitate',
    teaching_note: '88% of patients with tension PTX saved by needle decompression',
    common_mistake: 'Hesitating to perform - when in doubt, decompress'
  }
};

// ============================================================================
// CHEST SEAL
// ============================================================================

export const CHEST_SEAL = {
  code: 'CHEST_SEAL',
  name: 'Chest Seal Application',
  march_category: 'R',
  icon: ICONS.BANDAGE,
  indication: 'Open pneumothorax (sucking chest wound)',
  contraindications: ['Tension pneumothorax (need needle first)'],
  
  // Types
  types: {
    HYFIN_VENT: {
      name: 'Hyfin Vent Chest Seal',
      effectiveness: 0.92,
      vented: true,
      cost_usd: 15,
      icon: ICONS.GREEN
    },
    SAM_CHEST_SEAL: {
      name: 'SAM Chest Seal',
      effectiveness: 0.90,
      vented: true,
      cost_usd: 18,
      icon: ICONS.GREEN
    },
    BOLIN: {
      name: 'Bolin Chest Seal',
      effectiveness: 0.88,
      vented: true,
      cost_usd: 12,
      icon: ICONS.GREEN
    },
    IMPROVISED: {
      name: 'Improvised (plastic + tape)',
      effectiveness: 0.55,
      vented: false,
      notes: 'Burp every 2 minutes or convert to 3-sided seal',
      icon: ICONS.ORANGE
    }
  },
  
  // Procedure
  procedure: {
    application_time_seconds: 30,
    placement: 'Cover wound completely, seal all edges',
    monitoring: 'Watch for tension pneumothorax development'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    usage_rate: 0.12,
    open_ptx_incidence: 0.06,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training
  training: {
    minimum_level: 'COMBAT_LIFESAVER'
  },
  
  // Equipment
  equipment_per_casualty: {
    chest_seals: 2,
    cost_usd: 30
  },
  
  // Educational
  educational: {
    key_point: 'Use VENTED chest seal - reduces tension PTX risk',
    teaching_note: 'Entry AND exit wounds - need 2 seals',
    common_mistake: 'Using non-vented seal and not monitoring for tension PTX'
  }
};

// ============================================================================
// HYPOTHERMIA PREVENTION
// ============================================================================

export const HYPOTHERMIA_PREVENTION = {
  code: 'HYPOTHERMIA_PREVENTION',
  name: 'Hypothermia Prevention',
  march_category: 'H',
  icon: ICONS.SHIELD,
  indication: 'All trauma casualties',
  contraindications: ['Hyperthermia'],
  
  // Temperature thresholds
  temperature_thresholds: {
    normal: { temp_c: '>36', mortality_modifier: 1.0, icon: ICONS.GREEN },
    mild: { temp_c: '34-36', mortality_modifier: 1.16, icon: ICONS.YELLOW },
    moderate: { temp_c: '32-34', mortality_modifier: 1.32, icon: ICONS.ORANGE },
    severe: { temp_c: '<32', mortality_modifier: 1.65, icon: ICONS.RED }
  },
  
  // Prevention methods
  methods: {
    HYPOTHERMIA_PREVENTION_KIT: {
      name: 'Hypothermia Prevention Kit (HPK)',
      effectiveness: 0.85,
      components: ['Reflective blanket', 'Liner', 'Shell'],
      cost_usd: 35
    },
    READY_HEAT_BLANKET: {
      name: 'Ready Heat Blanket',
      effectiveness: 0.90,
      heat_duration_hours: 8,
      cost_usd: 45
    },
    IMPROVISED: {
      name: 'Improvised (uniforms, poncho)',
      effectiveness: 0.50,
      notes: 'Better than nothing'
    }
  },
  
  // Key actions
  key_actions: [
    'Remove wet clothing',
    'Insulate from ground',
    'Cover head (30% heat loss)',
    'Warm IV fluids if possible',
    'Minimize exposure time'
  ],
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    hypothermia_incidence: 0.35,
    winter_hypothermia_incidence: 0.65,
    mortality_increase_per_degree: 0.08,
    source: 'Ukraine 2022-2024 winter combat data'
  },
  
  // Training
  training: {
    minimum_level: 'TCCC_BASIC'
  },
  
  // Equipment
  equipment_per_casualty: {
    hypothermia_kits: 1,
    cost_usd: 35
  },
  
  // Educational
  educational: {
    key_point: 'Cold + Trauma = Death - hypothermia is lethal triad component',
    teaching_note: 'Each 1°C below 36°C = 8% increased mortality',
    common_mistake: 'Forgetting about hypothermia in warm weather (blood loss causes hypothermia)'
  }
};

// ============================================================================
// AIRWAY MANAGEMENT
// ============================================================================

export const AIRWAY_MANAGEMENT = {
  code: 'AIRWAY_MANAGEMENT',
  name: 'Airway Management',
  march_category: 'A',
  icon: ICONS.MEDICAL,
  
  // Interventions by level
  interventions: {
    NPA: {
      name: 'Nasopharyngeal Airway',
      training_level: 'TCCC_BASIC',
      effectiveness: 0.85,
      icon: ICONS.GREEN,
      contraindications: ['Mid-face trauma', 'Base of skull fracture'],
      procedure: 'Lubricate, insert perpendicular to face, advance to flange'
    },
    OPA: {
      name: 'Oropharyngeal Airway',
      training_level: 'COMBAT_LIFESAVER',
      effectiveness: 0.80,
      icon: ICONS.GREEN,
      contraindications: ['Conscious patient', 'Gag reflex present'],
      procedure: 'Insert inverted, rotate 180° as you advance'
    },
    SUPRAGLOTTIC: {
      name: 'Supraglottic Airway (iGel, King LT)',
      training_level: 'COMBAT_MEDIC',
      effectiveness: 0.92,
      icon: ICONS.YELLOW,
      notes: 'Requires training, excellent alternative to intubation'
    },
    CRICOTHYROIDOTOMY: {
      name: 'Surgical Cricothyroidotomy',
      training_level: 'PHYSICIAN_ASSISTANT',
      effectiveness: 0.95,
      icon: ICONS.ORANGE,
      notes: 'Last resort - cannot intubate, cannot ventilate',
      procedure: 'Vertical incision, horizontal membrane cut, insert tube'
    }
  },
  
  // Recovery position
  recovery_position: {
    indication: 'Unconscious patient with patent airway',
    position: 'Left lateral decubitus',
    purpose: 'Prevents aspiration, maintains airway'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    airway_compromise_rate: 0.08,
    npa_success_rate: 0.92,
    cric_rate: 0.002,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Educational
  educational: {
    key_point: 'Airway obstruction kills in 3-4 minutes',
    teaching_note: 'NPA is basic skill - every soldier should know',
    common_mistake: 'Not repositioning patient before advanced airway'
  }
};

// ============================================================================
// PELVIC BINDER
// ============================================================================

export const PELVIC_BINDER = {
  code: 'PELVIC_BINDER',
  name: 'Pelvic Binder Application',
  march_category: 'M',
  icon: ICONS.BANDAGE,
  indication: 'Suspected pelvic fracture, blast injury, lower extremity amputation',
  contraindications: ['Open pelvic fracture with viscera'],
  
  // Devices
  devices: {
    SAM_PELVIC_SLING: {
      name: 'SAM Pelvic Sling II',
      effectiveness: 0.78,
      cost_usd: 85,
      icon: ICONS.GREEN
    },
    T_POD: {
      name: 'T-POD Pelvic Stabilization Device',
      effectiveness: 0.75,
      cost_usd: 95,
      icon: ICONS.GREEN
    },
    IMPROVISED: {
      name: 'Improvised (sheet, belt)',
      effectiveness: 0.50,
      notes: 'Center over greater trochanters, tighten firmly',
      icon: ICONS.ORANGE
    }
  },
  
  // Procedure
  procedure: {
    application_time_seconds: 45,
    placement: 'Center over greater trochanters (NOT iliac crests)',
    tightening: 'Firm - reduce pelvis circumference'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    pelvic_fracture_rate: 0.12,
    mine_blast_pelvic_rate: 0.45,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training
  training: {
    minimum_level: 'COMBAT_LIFESAVER'
  },
  
  // Equipment
  equipment_per_casualty: {
    pelvic_binders: 1,
    cost_usd: 85
  },
  
  // Educational
  educational: {
    key_point: 'Pelvic fracture can cause 3L blood loss - bind it',
    teaching_note: 'Mine/blast = assume pelvic fracture until proven otherwise',
    common_mistake: 'Placing too high (iliac crests) instead of greater trochanters'
  }
};

// ============================================================================
// IV ACCESS
// ============================================================================

export const IV_ACCESS = {
  code: 'IV_ACCESS',
  name: 'IV/IO Access',
  march_category: 'C',
  icon: ICONS.SYRINGE,
  
  // Access types
  types: {
    PERIPHERAL_IV: {
      name: 'Peripheral IV',
      training_level: 'COMBAT_LIFESAVER',
      success_rate_first_attempt: 0.75,
      success_rate_hypotensive: 0.45,
      time_seconds: 60,
      icon: ICONS.GREEN
    },
    INTRAOSSEOUS: {
      name: 'Intraosseous (IO)',
      training_level: 'COMBAT_MEDIC',
      success_rate_first_attempt: 0.92,
      success_rate_hypotensive: 0.92,
      time_seconds: 30,
      preferred_in_shock: true,
      icon: ICONS.YELLOW,
      sites: ['Proximal tibia', 'Humeral head', 'Sternum']
    }
  },
  
  // IO devices
  io_devices: {
    EZ_IO: {
      name: 'EZ-IO Power Driver',
      effectiveness: 0.95,
      cost_usd: 250,
      needles_per_use: 1,
      needle_cost: 100
    },
    FAST1: {
      name: 'FAST1 Sternal',
      effectiveness: 0.90,
      cost_usd: 200
    },
    BIG: {
      name: 'Bone Injection Gun',
      effectiveness: 0.85,
      cost_usd: 150
    }
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    iv_success_rate: 0.68,
    io_usage_rate: 0.15,
    io_success_rate: 0.94,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training
  training: {
    minimum_level: 'COMBAT_LIFESAVER'
  },
  
  // Educational
  educational: {
    key_point: 'In shock, go IO first - do not waste time on failed IVs',
    teaching_note: 'IO has 92% success in hypotensive patients vs 45% for IV',
    common_mistake: 'Multiple IV attempts in shocked patient - just do IO'
  }
};

// ============================================================================
// BLOOD PRODUCTS
// ============================================================================

export const BLOOD_PRODUCTS = {
  code: 'BLOOD_PRODUCTS',
  name: 'Blood Product Administration',
  march_category: 'C',
  icon: ICONS.BLOOD,
  indication: 'Hemorrhagic shock, anticipated massive transfusion',
  contraindications: ['None in life-threatening hemorrhage'],
  
  // Products
  products: {
    WHOLE_BLOOD: {
      name: 'Fresh Whole Blood',
      effectiveness: 1.0,
      availability: 'LIMITED',
      storage: 'Walking Blood Bank',
      icon: ICONS.GREEN
    },
    LTOWB: {
      name: 'Low-Titer O Whole Blood',
      effectiveness: 0.95,
      availability: 'INCREASING',
      storage: 'Cold storage 21 days',
      icon: ICONS.GREEN
    },
    PRBC: {
      name: 'Packed Red Blood Cells',
      effectiveness: 0.80,
      availability: 'COMMON',
      storage: 'Cold storage 42 days',
      icon: ICONS.YELLOW,
      notes: 'Requires FFP and platelets for full effect'
    },
    FREEZE_DRIED_PLASMA: {
      name: 'Freeze-Dried Plasma',
      effectiveness: 0.75,
      availability: 'INCREASING',
      storage: 'Room temperature 2 years',
      icon: ICONS.YELLOW,
      notes: 'French FLYP, German Lyophilized Plasma'
    }
  },
  
  // Transfusion protocol
  transfusion_protocol: {
    trigger: 'SBP <90 or signs of shock',
    initial_dose: '1 unit whole blood or 1:1:1 components',
    target: 'SBP >90, HR <100, mentation improving'
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    whole_blood_usage_rate: 0.35,
    walking_blood_bank_activation: 0.28,
    mean_units_per_massive_transfusion: 24,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Training
  training: {
    minimum_level: 'COMBAT_MEDIC',
    requires_blood_typing: true
  },
  
  // Educational
  educational: {
    key_point: 'Blood saves lives - whole blood is best',
    teaching_note: 'Walking Blood Bank can provide 5-10 units in 30 minutes',
    common_mistake: 'Giving crystalloid instead of blood in hemorrhagic shock'
  }
};

// ============================================================================
// PAIN MANAGEMENT
// ============================================================================

export const PAIN_MANAGEMENT = {
  code: 'PAIN_MANAGEMENT',
  name: 'Pain Management',
  march_category: 'C',
  icon: ICONS.PILL,
  indication: 'Combat casualty with pain',
  
  // Options by consciousness
  options: {
    CONSCIOUS_MILD: {
      agent: 'Meloxicam 15mg PO',
      training_level: 'TCCC_BASIC',
      onset_minutes: 30,
      duration_hours: 24,
      icon: ICONS.GREEN
    },
    CONSCIOUS_MODERATE: {
      agent: 'OTFC (Fentanyl lozenge) 800mcg',
      training_level: 'COMBAT_MEDIC',
      onset_minutes: 5,
      duration_hours: 2,
      icon: ICONS.YELLOW,
      monitoring: 'Respiratory rate'
    },
    UNCONSCIOUS: {
      agent: 'Ketamine 50mg IV/IO or 100mg IM',
      training_level: 'COMBAT_MEDIC',
      onset_minutes: 2,
      duration_hours: 0.5,
      icon: ICONS.ORANGE,
      monitoring: 'Airway, emergence reactions',
      notes: 'Safe in shock - does not drop BP'
    }
  },
  
  // Ketamine protocol
  ketamine_protocol: {
    initial_iv: '20-30mg',
    initial_im: '50-100mg',
    maintenance: '10-20mg IV q10-15min',
    max_dose_24h_mg: 1000
  },
  
  // Ukraine data
  ukraine_data: {
    applicable: true,
    pain_management_rate: 0.65,
    ketamine_usage_rate: 0.42,
    source: 'Ukraine 2022-2024 combat medical data'
  },
  
  // Educational
  educational: {
    key_point: 'Pain control is humane and improves outcomes',
    teaching_note: 'Ketamine is safe in shock - use it',
    common_mistake: 'Withholding pain medication due to fear of respiratory depression'
  }
};

// ============================================================================
// EYE PROTECTION
// ============================================================================

export const EYE_PROTECTION = {
  code: 'EYE_PROTECTION',
  name: 'Eye Injury Management',
  march_category: 'H',
  icon: ICONS.SHIELD,
  indication: 'Penetrating eye injury, chemical exposure',
  
  // Interventions
  interventions: {
    RIGID_EYE_SHIELD: {
      name: 'Rigid Eye Shield',
      indication: 'Penetrating eye injury',
      technique: 'Cover, do NOT apply pressure',
      cost_usd: 5,
      icon: ICONS.GREEN
    },
    EYE_IRRIGATION: {
      name: 'Eye Irrigation',
      indication: 'Chemical exposure',
      volume_ml: 1000,
      duration_min: 15,
      icon: ICONS.YELLOW
    },
    MOIST_DRESSING: {
      name: 'Moist Sterile Dressing',
      indication: 'Exposed globe, avulsion',
      icon: ICONS.ORANGE
    }
  },
  
  // Ukraine data (drone injuries)
  ukraine_data: {
    applicable: true,
    eye_injury_incidence: 0.12,
    drone_related_eye_injuries: 0.18,
    bilateral_rate: 0.08,
    source: 'Ukraine 2022-2024 FPV drone casualty data'
  },
  
  // Training
  training: {
    minimum_level: 'TCCC_BASIC'
  },
  
  // Equipment
  equipment_per_casualty: {
    eye_shields: 2,
    cost_usd: 10
  },
  
  // Educational
  educational: {
    key_point: 'FPV drone injuries = high rate of eye injuries',
    teaching_note: '18% of drone casualties have eye injuries',
    common_mistake: 'Applying pressure to penetrating eye injury'
  }
};

// ============================================================================
// AGGREGATED INTERVENTIONS
// ============================================================================

export const ALL_INTERVENTIONS = {
  TOURNIQUET,
  TXA,
  HEMOSTATIC_DRESSING,
  NEEDLE_DECOMPRESSION,
  CHEST_SEAL,
  HYPOTHERMIA_PREVENTION,
  AIRWAY_MANAGEMENT,
  PELVIC_BINDER,
  IV_ACCESS,
  BLOOD_PRODUCTS,
  PAIN_MANAGEMENT,
  EYE_PROTECTION
};

export const INTERVENTION_CODES = Object.keys(ALL_INTERVENTIONS);

// ============================================================================
// NIGHT_FIRST_AID_MODIFIER
// EXT-07: Time multiplier applied to all CLS/first-aid response times during
// night operations. Reflects reduced visual assessment speed, slower movement,
// difficulty locating casualties, and equipment handling degradation.
//
// Applied as: effective_response_time = BASE_CLS_RESPONSE_MINUTES
//                                       × NIGHT_FIRST_AID_MODIFIER
//                                       × threat_delay_modifier
//
// Consumed by: survivalModule.js SVM-04 — replaces local `NIGHT_FIRST_AID_MODIFIER = 1.25`
// Reference: TCCC night operations studies; Ukraine 2022-2024 night casualty outcomes
// ============================================================================

export const NIGHT_FIRST_AID_MODIFIER = 1.25; // 25% longer response time at night

// ============================================================================
// BASE_CLS_RESPONSE_MINUTES
// EXT-07: Baseline minutes from wounding to first CLS contact under ideal
// conditions: daylight, low threat, trained CLS immediately present, no
// terrain obstacles.
//
// All additional delays (night, threat, terrain) are multiplied on top of this.
// Effective response = BASE_CLS_RESPONSE_MINUTES
//                    × NIGHT_FIRST_AID_MODIFIER (if night)
//                    × THREAT_LEVELS[threat].medical.first_aid_delay_minutes / BASE_CLS_RESPONSE_MINUTES
//
// Consumed by: survivalModule.js SVM-05 — replaces `baseTimeMinutes = 3` literal
// Reference: TCCC response time standards; NATO medical exercise data
// ============================================================================

export const BASE_CLS_RESPONSE_MINUTES = 3; // minutes — ideal conditions baseline

// ============================================================================
// DOW_REDUCTION_RATES
// EXT-07: Died of Wounds (DOW) reduction fractions by prehospital intervention tier.
// Represents the proportional reduction in DOW rate achieved by each tier of care,
// compared to no intervention.
//
// Values as decimal fractions (×100 for percent):
//   TOURNIQUET_ONLY = 0.25 → 25% DOW reduction (hemorrhage control only)
//   FULL_TCCC       = 0.40 → 40% DOW reduction (tourniquet + TXA + airway + shock)
//
// Consumed by: survivalModule.js SVM-08 — replaces `dowReductionPercent` literals 25 and 40
//
// Sources: CRASH-2 trial (TXA +15%), CoTCCC tourniquet data (+20-25%),
//   Kotwal et al. TCCC systematic review; USSOCOM TCCC outcomes 2001-2020;
//   Ukraine 2022-2024 preventable death analysis
// ============================================================================

export const DOW_REDUCTION_RATES = {
  // Tourniquet application only (no TXA, no airway management)
  TOURNIQUET_ONLY: 0.25,  // 25% reduction — hemorrhage control primary effect

  // Full TCCC protocol: tourniquet + hemostatic dressing + TXA + NPA + hypothermia prevention
  FULL_TCCC:       0.40   // 40% reduction — ceiling of prehospital intervention benefit
};

// ============================================================================
// UI HELPER FUNCTIONS
// ============================================================================

/**
 * Get intervention options for UI dropdown
 * @returns {Array} Intervention options
 */
export const getInterventionOptions = () => {
  return Object.entries(ALL_INTERVENTIONS).map(([key, intervention]) => ({
    value: key,
    label: intervention.name,
    icon: intervention.icon,
    marchCategory: intervention.march_category,
    indication: intervention.indication,
    trainingLevel: intervention.training?.minimum_level
  }));
};

/**
 * Get interventions by MARCH category
 * @param {string} category - MARCH category (M, A, R, C, H)
 * @returns {Array} Interventions in that category
 */
export const getInterventionsByMARCH = (category) => {
  return Object.entries(ALL_INTERVENTIONS)
    .filter(([_, intervention]) => intervention.march_category === category)
    .map(([key, intervention]) => ({
      value: key,
      label: intervention.name,
      icon: intervention.icon
    }));
};

/**
 * Get intervention by code
 * @param {string} code - Intervention code
 * @returns {Object} Intervention object
 */
export const getIntervention = (code) => {
  return ALL_INTERVENTIONS[code] || null;
};

// ============================================================================
// CALCULATION FUNCTIONS
// ============================================================================

/**
 * Calculate tourniquet effectiveness by time
 * @param {number} minutes - Time since injury
 * @returns {Object} Effectiveness data
 */
export const calculateTourniquetEffectiveness = (minutes) => {
  if (minutes <= 2) return { rate: 0.95, icon: ICONS.GREEN, category: 'OPTIMAL' };
  if (minutes <= 5) return { rate: 0.87, icon: ICONS.GREEN, category: 'GOOD' };
  if (minutes <= 10) {
    const rate = 0.87 - ((minutes - 5) / 5) * (0.87 - 0.75);
    return { rate, icon: ICONS.YELLOW, category: 'ACCEPTABLE' };
  }
  if (minutes <= 15) {
    const rate = 0.75 - ((minutes - 10) / 5) * (0.75 - 0.64);
    return { rate, icon: ICONS.ORANGE, category: 'DEGRADED' };
  }
  if (minutes <= 30) {
    const rate = 0.64 - ((minutes - 15) / 15) * (0.64 - 0.28);
    return { rate, icon: ICONS.RED, category: 'POOR' };
  }
  return { rate: 0.28, icon: ICONS.RED, category: 'CRITICAL' };
};

/**
 * Calculate TXA effectiveness by time
 * @param {number} minutes - Time since injury
 * @returns {Object} Effectiveness data
 */
export const calculateTXAEffectiveness = (minutes) => {
  if (minutes <= 60) return { rate: 0.32, icon: ICONS.GREEN, category: 'OPTIMAL', harmful: false };
  if (minutes <= 120) {
    const rate = 0.32 - ((minutes - 60) / 60) * (0.32 - 0.21);
    return { rate, icon: ICONS.YELLOW, category: 'DEGRADED', harmful: false };
  }
  if (minutes <= 180) {
    const rate = 0.21 - ((minutes - 120) / 60) * 0.21;
    return { rate, icon: ICONS.ORANGE, category: 'MARGINAL', harmful: false };
  }
  return { rate: -0.08, icon: ICONS.RED, category: 'HARMFUL', harmful: true };
};

/**
 * Calculate hypothermia mortality multiplier
 * @param {number} tempCelsius - Body temperature
 * @returns {Object} Mortality multiplier data
 */
export const calculateHypothermiaMortalityMultiplier = (tempCelsius) => {
  if (tempCelsius >= 36) return { multiplier: 1.0, icon: ICONS.GREEN, category: 'NORMAL' };
  if (tempCelsius >= 34) {
    const mult = 1.0 + ((36 - tempCelsius) * 0.08);
    return { multiplier: mult, icon: ICONS.YELLOW, category: 'MILD' };
  }
  if (tempCelsius >= 32) {
    const mult = 1.16 + ((34 - tempCelsius) * 0.08);
    return { multiplier: mult, icon: ICONS.ORANGE, category: 'MODERATE' };
  }
  const mult = 1.32 + ((32 - tempCelsius) * 0.08);
  return { multiplier: Math.min(mult, 2.0), icon: ICONS.RED, category: 'SEVERE' };
};

/**
 * Get indicated interventions by injury mechanism
 * @param {string} mechanism - Injury mechanism
 * @returns {Array} Indicated interventions
 */
export const getIndicatedInterventions = (mechanism) => {
  const interventions = ['HYPOTHERMIA_PREVENTION'];
  
  switch (mechanism) {
    case 'ARTILLERY':
      interventions.push('TOURNIQUET', 'TXA', 'HEMOSTATIC_DRESSING', 'NEEDLE_DECOMPRESSION');
      break;
    case 'DRONE':
    case 'FPV_DRONE':
      interventions.push('AIRWAY_MANAGEMENT', 'HEMOSTATIC_DRESSING', 'EYE_PROTECTION');
      break;
    case 'MINE':
    case 'MINE_IED':
      interventions.push('TOURNIQUET', 'TXA', 'PELVIC_BINDER', 'BLOOD_PRODUCTS');
      break;
    case 'SMALL_ARMS':
      interventions.push('TOURNIQUET', 'TXA', 'CHEST_SEAL');
      break;
    case 'BLAST':
      interventions.push('NEEDLE_DECOMPRESSION', 'CHEST_SEAL', 'TXA');
      break;
    case 'VEHICLE_FIRE':
      interventions.push('AIRWAY_MANAGEMENT', 'IV_ACCESS', 'PAIN_MANAGEMENT');
      break;
    default:
      interventions.push('TOURNIQUET', 'TXA');
  }
  
  return interventions.map(code => ({
    code,
    ...ALL_INTERVENTIONS[code]
  }));
};

/**
 * Calculate combined intervention survival effect
 * @param {Array} interventions - Array of {type, time_applied}
 * @param {number} baselineMortality - Baseline mortality rate
 * @returns {Object} Survival calculation
 * 
 * BUG-029 FIX (January 20, 2026):
 * - Added Array.isArray() check to prevent "{} is not iterable" error
 * - survivalModule.js may pass {} instead of [] when no interventions configured
 */
export const calculateCombinedInterventionEffect = (interventions, baselineMortality) => {
  // BUG-029 FIX: Ensure baselineMortality has a valid default
  const safeMortality = typeof baselineMortality === 'number' && !isNaN(baselineMortality) 
    ? baselineMortality 
    : 0.15; // Default 15% mortality baseline
  
  let mortality = safeMortality;
  const effects = [];
  
  // BUG-029 FIX: Ensure interventions is an array, not {} or null
  const safeInterventions = Array.isArray(interventions) ? interventions : [];
  
  // Return early if no interventions
  if (safeInterventions.length === 0) {
    return {
      baseline_mortality: safeMortality,
      final_mortality: safeMortality,
      survival_rate: 1 - safeMortality,
      effects: [],
      icon: ICONS.YELLOW,
      no_interventions: true
    };
  }
  
  for (const intervention of safeInterventions) {
    const { type, time_applied } = intervention;
    let effect = 0;
    
    switch (type) {
      case 'TOURNIQUET':
        const tqData = calculateTourniquetEffectiveness(time_applied);
        effect = tqData.rate;
        mortality = mortality * (1 - effect);
        effects.push({ type, effect, icon: tqData.icon });
        break;
      case 'TXA':
        const txaData = calculateTXAEffectiveness(time_applied);
        if (!txaData.harmful) {
          mortality = mortality * (1 - txaData.rate);
          effects.push({ type, effect: txaData.rate, icon: txaData.icon });
        } else {
          mortality = mortality * (1 + Math.abs(txaData.rate));
          effects.push({ type, effect: txaData.rate, icon: txaData.icon, harmful: true });
        }
        break;
      case 'HEMOSTATIC_DRESSING':
        effect = 0.24 * 0.81;
        mortality = mortality * (1 - effect);
        effects.push({ type, effect, icon: ICONS.GREEN });
        break;
      case 'NEEDLE_DECOMPRESSION':
        effect = 0.88;
        mortality = mortality * (1 - effect);
        effects.push({ type, effect, icon: ICONS.GREEN });
        break;
      case 'CHEST_SEAL':
        effect = 0.45;
        mortality = mortality * (1 - effect);
        effects.push({ type, effect, icon: ICONS.GREEN });
        break;
      case 'PELVIC_BINDER':
        effect = 0.22;
        mortality = mortality * (1 - effect);
        effects.push({ type, effect, icon: ICONS.GREEN });
        break;
      default:
        // Unknown intervention type - no effect but log for debugging
        effects.push({ type, effect: 0, icon: ICONS.YELLOW, unknown: true });
        break;
    }
  }
  
  return {
    baseline_mortality: safeMortality,
    final_mortality: Math.max(0, Math.min(1, mortality)),
    survival_rate: Math.max(0, Math.min(1, 1 - mortality)),
    effects,
    icon: mortality < safeMortality * 0.5 ? ICONS.GREEN : ICONS.YELLOW
  };
};

/**
 * Calculate equipment requirements for casualty count
 * @param {number} casualtyCount - Number of casualties
 * @param {Object} triageDistribution - Distribution by triage category
 * @returns {Object} Equipment requirements
 */
export const calculateEquipmentRequirements = (casualtyCount, triageDistribution = null) => {
  const dist = triageDistribution || { T1: 0.37, T2: 0.38, T3: 0.18, T4: 0.07 };
  
  const t1 = Math.round(casualtyCount * dist.T1);
  const t2 = Math.round(casualtyCount * dist.T2);
  const t3 = Math.round(casualtyCount * dist.T3);
  
  const requirements = {
    tourniquets: Math.ceil((t1 + t2 * 0.5) * 2),
    hemostatic_gauze: Math.ceil((t1 + t2 * 0.3) * 2),
    chest_seals: Math.ceil((t1 + t2 * 0.2) * 2),
    needle_decompression: Math.ceil(t1 * 0.08 * 2),
    npa: Math.ceil((t1 + t2) * 1.2),
    iv_kits: Math.ceil((t1 + t2) * 2),
    io_devices: Math.ceil(t1 * 0.3),
    txa_vials: Math.ceil((t1 + t2 * 0.5) * 2),
    pelvic_binders: Math.ceil((t1 + t2) * 0.15),
    hypothermia_kits: Math.ceil((t1 + t2 + t3) * 0.8),
    eye_shields: Math.ceil((t1 + t2) * 0.12 * 2),
    pain_meds: Math.ceil((t1 + t2 + t3 * 0.5))
  };
  
  // Calculate total cost
  const unitCosts = {
    tourniquets: 30, hemostatic_gauze: 45, chest_seals: 15,
    needle_decompression: 10, npa: 5, iv_kits: 15, io_devices: 100,
    txa_vials: 17.50, pelvic_binders: 85, hypothermia_kits: 35,
    eye_shields: 5, pain_meds: 25
  };
  
  let totalCost = 0;
  for (const [item, count] of Object.entries(requirements)) {
    totalCost += count * (unitCosts[item] || 0);
  }
  
  requirements.total_cost_usd = Math.round(totalCost);
  requirements.cost_per_casualty = Math.round(totalCost / casualtyCount);
  
  return requirements;
};

/**
 * Get required training level for intervention
 * @param {string} interventionCode - Intervention code
 * @returns {Object} Training level data
 */
export const getRequiredTrainingLevel = (interventionCode) => {
  const intervention = ALL_INTERVENTIONS[interventionCode];
  if (!intervention) return null;
  
  const levelCode = intervention.training?.minimum_level || 'TCCC_BASIC';
  const level = TRAINING_LEVELS[levelCode];
  
  return {
    code: levelCode,
    name: level?.name || levelCode,
    icon: level?.icon || ICONS.GREEN,
    hours: level?.hours || 0
  };
};

/**
 * Check if intervention is indicated for mechanism
 * @param {string} interventionCode - Intervention code
 * @param {string} mechanism - Injury mechanism
 * @returns {boolean} True if indicated
 */
export const isInterventionIndicated = (interventionCode, mechanism) => {
  const indicated = getIndicatedInterventions(mechanism);
  return indicated.some(i => i.code === interventionCode);
};

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

/**
 * Validate intervention configuration
 * @param {Object} config - Configuration to validate
 * @returns {Object} Validation result
 */
export const validateInterventionConfig = (config) => {
  const errors = [];
  const warnings = [];
  
  // Validate intervention code
  if (config.intervention && !ALL_INTERVENTIONS[config.intervention]) {
    errors.push({
      field: 'intervention',
      message: `Invalid intervention: ${config.intervention}`,
      icon: ICONS.RED
    });
  }
  
  // Validate training level
  if (config.trainingLevel && !TRAINING_LEVELS[config.trainingLevel]) {
    errors.push({
      field: 'trainingLevel',
      message: `Invalid training level: ${config.trainingLevel}`,
      icon: ICONS.RED
    });
  }
  
  // Check training level matches intervention requirement
  if (config.intervention && config.trainingLevel) {
    const required = getRequiredTrainingLevel(config.intervention);
    const trainingOrder = ['TCCC_BASIC', 'COMBAT_LIFESAVER', 'COMBAT_MEDIC', 'PHYSICIAN_ASSISTANT', 'PHYSICIAN'];
    const requiredIndex = trainingOrder.indexOf(required.code);
    const providedIndex = trainingOrder.indexOf(config.trainingLevel);
    
    if (providedIndex < requiredIndex) {
      warnings.push({
        field: 'trainingLevel',
        message: `${config.intervention} requires ${required.name} training`,
        icon: ICONS.WARNING
      });
    }
  }
  
  // TXA timing warning
  if (config.intervention === 'TXA' && config.timeSinceInjury > 180) {
    errors.push({
      field: 'timing',
      message: 'TXA after 3 hours is HARMFUL - do not administer',
      icon: ICONS.RED
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    icon: errors.length > 0 ? ICONS.RED : (warnings.length > 0 ? ICONS.YELLOW : ICONS.GREEN)
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const prehospitalInterventionsConstants = {
  // Algorithm
  MARCH_ALGORITHM,
  TRAINING_LEVELS,
  
  // Individual interventions
  TOURNIQUET,
  TXA,
  HEMOSTATIC_DRESSING,
  NEEDLE_DECOMPRESSION,
  CHEST_SEAL,
  HYPOTHERMIA_PREVENTION,
  AIRWAY_MANAGEMENT,
  PELVIC_BINDER,
  IV_ACCESS,
  BLOOD_PRODUCTS,
  PAIN_MANAGEMENT,
  EYE_PROTECTION,
  
  // Aggregated
  ALL_INTERVENTIONS,
  INTERVENTION_CODES,
  NIGHT_FIRST_AID_MODIFIER,
  BASE_CLS_RESPONSE_MINUTES,
  DOW_REDUCTION_RATES,
  
  // UI Options
  getMARCHOptions,
  getTrainingLevelOptions,
  getInterventionOptions,
  getInterventionsByMARCH,
  getIntervention,
  
  // Calculations
  calculateTourniquetEffectiveness,
  calculateTXAEffectiveness,
  calculateHypothermiaMortalityMultiplier,
  getIndicatedInterventions,
  calculateCombinedInterventionEffect,
  calculateEquipmentRequirements,
  getRequiredTrainingLevel,
  isInterventionIndicated,
  
  // Validation
  validateInterventionConfig
};

export default prehospitalInterventionsConstants;
